"use client";

import { c as _c } from "react/compiler-runtime";
import { clsx } from "@/helpers/clsx";
import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import backed from "public/_static/customers/backed.svg";
import connect from "public/_static/customers/connect.svg";
import creandum from "public/_static/customers/creandum.svg";
import heartcore from "public/_static/customers/heartcore.svg";
import heliad from "public/_static/customers/heliad.svg";
import icebreaker from "public/_static/customers/icebreaker.svg";
import inventure from "public/_static/customers/inventure.svg";
import j12 from "public/_static/customers/j12.svg";
import msad from "public/_static/customers/msad.svg";
import peopleventures from "public/_static/customers/people-ventures.svg";
import transition from "public/_static/customers/transition.svg";
import { AnimatedShinyText } from "./AnimatedShinyText";
const HEIGHT = 15;
type LogoConfig = {
  src: any;
  alt: string;
  height: number;
  className?: string;
} | {
  src?: never;
  title: string;
  className?: string;
};
const LOGOS = [{
  src: j12,
  alt: "J12 logo",
  height: HEIGHT + 14
}, {
  src: backed,
  alt: "Backed.vc logo",
  height: HEIGHT
}, {
  src: connect,
  alt: "Connect Ventures logo",
  height: HEIGHT
}, {
  src: creandum,
  alt: "Creandum logo",
  height: HEIGHT - 1,
  className: "mt-px"
}, {
  src: heartcore,
  alt: "Heartcore logo",
  height: HEIGHT + 18
}, {
  src: heliad,
  alt: "Heliad logo",
  height: HEIGHT + 2,
  className: "-mt-0.5"
}, {
  src: icebreaker,
  alt: "Icebreaker logo",
  height: HEIGHT + 3,
  className: "-mt-0.5"
}, {
  src: inventure,
  alt: "Inventure logo",
  height: HEIGHT + 24
}, {
  title: "$2B+ AUM Fund"
}, {
  src: msad,
  alt: "MS&AD logo",
  height: HEIGHT
}, {
  src: peopleventures,
  alt: "People Ventures logo",
  height: HEIGHT + 6,
  className: "mt-0.5"
}, {
  title: "$5B+ AUM Fund"
}, {
  src: transition,
  alt: "Transition logo",
  height: HEIGHT
}] satisfies ReadonlyArray<LogoConfig>;

/**
 * The row starts from this logo and wraps around, so the ones before it come
 * last. The marquee scrolls left, so a logo near the start is on screen at load
 * and leaves almost at once; starting just after Creandum brings it in from
 * the right a few seconds after the page mounts instead.
 */
const FIRST_LOGO = "MS&AD logo";
const firstIndex = Math.max(0, LOGOS.findIndex(logo => "alt" in logo && logo.alt === FIRST_LOGO));
const ORDERED_LOGOS = [...LOGOS.slice(firstIndex), ...LOGOS.slice(0, firstIndex)];
const LogoRow = t0 => {
  const $ = _c(7);
  const {
    ariaHidden: t1,
    floating: t2
  } = t0;
  const ariaHidden = t1 === undefined ? false : t1;
  const floating = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clsx("flex items-center gap-16 pr-14 text-black *:flex-shrink-0");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== ariaHidden || $[2] !== floating) {
    t4 = ORDERED_LOGOS.map((logo, index) => <motion.div key={`${logo.alt || logo.title}-${ariaHidden ? "dup" : "main"}`} className="will-change-transform" animate={floating ? {
      y: [0, -2, 0],
      opacity: [0.78, 1, 0.78]
    } : undefined} transition={floating ? {
      duration: 3.6 + index % 3 * 0.45,
      delay: index * 0.12,
      repeat: Infinity,
      ease: "easeInOut"
    } : undefined}>{logo.src ? <Image src={logo.src} alt={logo.alt} height={logo.height} className={logo.className} /> : <div className={clsx("text-md border-[1.5px] border-white px-3 py-0.5 font-semibold text-white", logo.className)}>{logo.title}</div>}</motion.div>);
    $[1] = ariaHidden;
    $[2] = floating;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== ariaHidden || $[5] !== t4) {
    t5 = <div className={t3} aria-hidden={ariaHidden}>{t4}</div>;
    $[4] = ariaHidden;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
export const LogoWall = () => {
  const $ = _c(15);
  const reduceMotion = useReducedMotion();
  let t0;
  if ($[0] !== reduceMotion) {
    t0 = reduceMotion ? false : {
      opacity: 0,
      y: 8
    };
    $[0] = reduceMotion;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== reduceMotion) {
    t1 = reduceMotion ? undefined : {
      opacity: 1,
      y: 0
    };
    $[2] = reduceMotion;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true,
      amount: 0.7
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1]
    };
    t4 = <AnimatedShinyText className="text-[13px] tracking-widest">Trusted by leading VCs</AnimatedShinyText>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t0 || $[8] !== t1) {
    t5 = <motion.div initial={t0} whileInView={t1} viewport={t2} transition={t3}>{t4}</motion.div>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== reduceMotion) {
    t6 = <div className="relative -my-1 w-full overflow-hidden py-1">{reduceMotion ? <div className="flex justify-center"><LogoRow /></div> : <motion.div className="flex w-max will-change-transform" animate={{
        x: ["0%", "-50%"]
      }} transition={{
        x: {
          duration: 28,
          repeat: Infinity,
          ease: "linear"
        }
      }}><LogoRow /><LogoRow ariaHidden={true} /></motion.div>}</div>;
    $[10] = reduceMotion;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="fade-edges mb-3 flex w-full max-w-[1216px] flex-col items-center gap-3.5 overflow-hidden">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};